import React from 'react';
import { useUser } from '@clerk/clerk-react';
import type { BootstrapResponse } from '../types/identity';
import type { OrgStatus } from '../types/organization';
import { useBffClient } from '../api/client';
import { useCurrentOrgContext } from '../hooks/use-current-org';

interface IdentityGateProps {
  children: React.ReactNode;
  onNeedsOnboarding: () => void;
  onNeedsApproval?: (orgName: string, orgId: string) => void;
}

export function IdentityGate({ children, onNeedsOnboarding, onNeedsApproval }: IdentityGateProps) {
  const { user, isLoaded: isUserLoaded } = useUser();
  const client = useBffClient();
  const { setCurrentOrg } = useCurrentOrgContext();
  const [state, setState] = React.useState<'loading' | 'ready' | 'onboarding' | 'awaiting-approval' | 'error'>('loading');
  const [retryCount, setRetryCount] = React.useState(0);
  const hasBootstrapped = React.useRef(false);
  const onNeedsOnboardingRef = React.useRef(onNeedsOnboarding);
  onNeedsOnboardingRef.current = onNeedsOnboarding;
  const onNeedsApprovalRef = React.useRef(onNeedsApproval);
  onNeedsApprovalRef.current = onNeedsApproval;

  React.useEffect(() => {
    if (!isUserLoaded || !user || hasBootstrapped.current) return;
    hasBootstrapped.current = true;

    const email = user.primaryEmailAddress?.emailAddress ?? '';
    const displayName = user.fullName ?? user.firstName ?? email.split('@')[0];

    client.post<BootstrapResponse>('/bff/identity/bootstrap', { email, displayName })
      .then((data) => {
        if (data.organizations.length > 0) {
          const storedOrgId = localStorage.getItem('redbax_current_org');
          const matchedOrg = storedOrgId
            ? data.organizations.find(o => o.orgId === storedOrgId)
            : null;
          const org = matchedOrg ?? data.organizations[0];
          const status = org.status as OrgStatus;

          // A suspended org is NOT the same as a not-yet-approved org: the user is a
          // legitimate member and still enters the app, where a suspended banner is
          // shown. Only genuinely pending/provisioning orgs go to the approval screen.
          //
          // Upgrading belongs with those two for the same reason (story #136). It lasts seconds, and
          // bouncing someone to the approval screen in the middle of paying - then back again - would
          // make a successful upgrade look like their account had been reset.
          if (status !== 'Active' && status !== 'Suspended' && status !== 'Upgrading') {
            onNeedsApprovalRef.current?.(org.name, org.orgId);
            setState('awaiting-approval');
            return;
          }

          setCurrentOrg({
            orgId: org.orgId, name: org.name, role: org.role, status, isPersonal: org.isPersonal,
          });
          setState('ready');
        } else {
          onNeedsOnboardingRef.current();
          setState('onboarding');
        }
      })
      .catch(() => {
        // Fail closed: we could not confirm the user's identity/org membership,
        // so we must NOT grant access to the app. Block and offer a retry.
        setState('error');
      });
  }, [isUserLoaded, user, retryCount]);

  if (state === 'error') {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background">
        <div className="text-text-secondary">We couldn't load your workspace. Please try again.</div>
        <button
          type="button"
          onClick={() => {
            hasBootstrapped.current = false;
            setState('loading');
            setRetryCount((c) => c + 1);
          }}
          className="rounded-md border border-border px-4 py-2 text-sm text-text-primary hover:bg-surface"
        >
          Try again
        </button>
      </div>
    );
  }

  if (state !== 'ready') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-text-secondary">Setting up your workspace...</div>
      </div>
    );
  }

  return <>{children}</>;
}
